import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Loader2, Rocket, Wallet } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { CustomerCombobox } from "../../components/admin/CustomerCombobox";
import { PaymentRecordingModal } from "../../components/admin/PaymentRecordingModal";
import { openWhatsApp, waTemplates } from "../../lib/whatsapp";

const DEFAULT_VAT = 15;
const money = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

const blankLine = () => ({ date: "", vehicle: "", client: "", insurer: "", amount_excl: 0, vat: 0, amount_incl: 0 });

export const AdminInvoiceForm = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;

  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({
    vat_pct: DEFAULT_VAT,
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    reference: "",
    notes: "",
    lines: [blankLine()],
    status: "draft",
    invoice_number: null,
  });
  const [busy, setBusy] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const inv = await adminApi.getInvoice(id);
        setForm({
          vat_pct: inv.vat_pct ?? DEFAULT_VAT,
          date: (inv.date || "").slice(0, 10),
          due_date: (inv.due_date || "").slice(0, 10),
          reference: inv.reference || "",
          notes: inv.notes || "",
          lines: (inv.lines || []).length ? inv.lines : [blankLine()],
          status: inv.status,
          invoice_number: inv.invoice_number,
          customer_id: inv.customer_id,
          customer_name: inv.customer_name,
          customer_code: inv.customer_code,
        });
        if (inv.customer_id) {
          try {
            const c = await adminApi.getCustomer(inv.customer_id);
            setCustomer(c);
          } catch { /* silent */ }
        }
      } catch {
        toast.error("Could not load invoice");
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const readonly = form.status !== "draft";

  // ---- Line editing with bidirectional VAT calc ----
  const updateLine = (idx, patch) => {
    setForm((f) => {
      const lines = [...f.lines];
      const before = { ...lines[idx], ...patch };
      // Detect which side was edited — recompute the other two:
      const vatRate = Number(f.vat_pct || 0) / 100;
      let excl = Number(before.amount_excl || 0);
      let vat = Number(before.vat || 0);
      let incl = Number(before.amount_incl || 0);
      if ("amount_excl" in patch) {
        vat = r2(excl * vatRate);
        incl = r2(excl + vat);
      } else if ("amount_incl" in patch) {
        excl = r2(incl / (1 + vatRate || 1));
        vat = r2(incl - excl);
      } else if ("vat" in patch) {
        // rarely edited — keep excl, adjust incl
        incl = r2(excl + vat);
      }
      lines[idx] = { ...before, amount_excl: excl, vat, amount_incl: incl };
      return { ...f, lines };
    });
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const rmLine = (idx) => setForm((f) => {
    const remaining = f.lines.filter((_, i) => i !== idx);
    return { ...f, lines: remaining.length ? remaining : [blankLine()] };
  });

  // Recompute all lines when VAT % changes
  const changeVatPct = (v) => {
    setForm((f) => {
      const rate = Number(v || 0) / 100;
      const lines = (f.lines || []).map((ln) => {
        const excl = Number(ln.amount_excl || 0);
        const vat = r2(excl * rate);
        return { ...ln, vat, amount_incl: r2(excl + vat) };
      });
      return { ...f, vat_pct: Number(v || 0), lines };
    });
  };

  const totals = form.lines.reduce((acc, l) => ({
    excl: acc.excl + Number(l.amount_excl || 0),
    vat: acc.vat + Number(l.vat || 0),
    incl: acc.incl + Number(l.amount_incl || 0),
  }), { excl: 0, vat: 0, incl: 0 });

  // ---- Actions ----
  const payload = () => ({
    customer_id: customer?.id || null,
    customer_name: customer?.name || form.customer_name || null,
    customer_code: customer?.code || form.customer_code || null,
    billing_address: customer?.billing_address || null,
    vat_number: customer?.vat_number || null,
    vat_pct: Number(form.vat_pct || 0),
    date: form.date,
    due_date: form.due_date || null,
    reference: form.reference || null,
    notes: form.notes || null,
    lines: form.lines,
  });

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (editing) {
        await adminApi.updateInvoice(id, payload());
        toast.success("Draft saved");
      } else {
        const inv = await adminApi.createInvoice(payload());
        toast.success("Draft created");
        nav(`/admin/invoices/${inv.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const issue = async () => {
    if (!totals.incl) {
      toast.error("Add at least one line with an amount");
      return;
    }
    if (!window.confirm("Issue this invoice? A sequential INV#### number will be allocated and the invoice becomes read-only.")) return;
    setBusy(true);
    try {
      // Save first so latest edits are on the record
      let invId = id;
      if (editing) {
        await adminApi.updateInvoice(id, payload());
      } else {
        const created = await adminApi.createInvoice(payload());
        invId = created.id;
      }
      const res = await adminApi.issueInvoice(invId);
      toast.success(`Invoice ${res.invoice_number} issued`);
      nav(`/admin/invoices/${invId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-invoice-form">
      <Link to="/admin/invoices" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
        <ArrowLeft size={14} /> Back to invoices
      </Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
            {form.invoice_number ? "Issued invoice" : "Draft invoice"}
          </div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">
            {form.invoice_number || "New Invoice"}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readonly && (
            <>
              <button
                onClick={saveDraft}
                disabled={busy}
                data-testid="save-draft-btn"
                className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#f5f6f6] disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft
              </button>
              <button
                onClick={issue}
                disabled={busy}
                data-testid="issue-invoice-btn"
                className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
              >
                <Rocket size={14} /> Issue invoice
              </button>
            </>
          )}
          {form.invoice_number && (
            <>
              <button
                type="button"
                onClick={() => setShowPayment(true)}
                data-testid="record-payment-btn"
                className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
              >
                <Wallet size={14} /> Payments
              </button>
              <button
                type="button"
                onClick={() => {
                  const customerObj = customer || {};
                  const phone = customerObj.mobile || customerObj.telephone;
                  if (!phone) { toast.error("No mobile on file for this customer"); return; }
                  openWhatsApp(phone, waTemplates.status_invoiced({
                    client_name: customerObj.name || form.customer_name,
                    tax_invoice_number: form.invoice_number,
                    portal_url: "",
                  }));
                }}
                className="bg-[#25D366] text-white font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
                data-testid="invoice-whatsapp-btn"
                title="Send this invoice via WhatsApp — free"
              >
                WhatsApp
              </button>
              <button
                onClick={() => adminApi.openDoc(adminApi.invoicePdfUrl(id))}
                type="button"
                className="bg-[#20282c] text-white font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110"
                data-testid="download-invoice-pdf"
              >
                Download PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Customer + meta */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#cfd7db] rounded-md p-5 space-y-3">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Customer</div>
          {readonly ? (
            <div className="bg-[#f5f6f6] border border-[#cfd7db] rounded-md p-3">
              <div className="font-bold text-[#111619]">{form.customer_name || "—"}</div>
              {form.customer_code && <div className="text-[11px] text-[#3f4b52]">{form.customer_code}</div>}
            </div>
          ) : (
            <CustomerCombobox value={customer} onChange={setCustomer} testid="invoice-customer-picker" />
          )}
        </div>

        <div className="bg-white border border-[#cfd7db] rounded-md p-5 grid grid-cols-2 gap-3">
          <Field label="Invoice date">
            <input type="date" value={form.date} disabled={readonly}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm disabled:bg-[#f5f6f6]"
              data-testid="invoice-date-input" />
          </Field>
          <Field label="Due date">
            <input type="date" value={form.due_date} disabled={readonly}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm disabled:bg-[#f5f6f6]" />
          </Field>
          <Field label="Reference">
            <input value={form.reference} disabled={readonly}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="e.g. Feb 2026 batch"
              className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm disabled:bg-[#f5f6f6]"
              data-testid="invoice-reference-input" />
          </Field>
          <Field label="VAT %">
            <input type="number" step="0.01" value={form.vat_pct} disabled={readonly}
              onChange={(e) => changeVatPct(e.target.value)}
              className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm disabled:bg-[#f5f6f6]"
              data-testid="invoice-vat-input" />
          </Field>
        </div>
      </div>

      {/* Line items */}
      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#cfd7db] flex items-center justify-between">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Line items (fitment history)</div>
          {!readonly && (
            <button onClick={addLine} data-testid="add-line-btn"
              className="text-xs text-[#f28a16] hover:underline inline-flex items-center gap-1">
              <Plus size={12} /> Add line
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#f5f6f6] text-[#3f4b52]">
              <tr>
                <Th>#</Th>
                <Th>Date</Th>
                <Th>Vehicle</Th>
                <Th>Client</Th>
                <Th>Insurer / Payer</Th>
                <Th className="text-right">Excl.</Th>
                <Th className="text-right">VAT</Th>
                <Th className="text-right">Incl.</Th>
                {!readonly && <Th></Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eef2f4]">
              {form.lines.map((ln, i) => (
                <tr key={i} data-testid={`invoice-line-${i}`}>
                  <Td className="text-center text-[#3f4b52]">{i + 1}</Td>
                  <Td><Inp type="date" val={ln.date} on={(v) => updateLine(i, { date: v })} test={`line-${i}-date`} readonly={readonly} /></Td>
                  <Td><Inp val={ln.vehicle} on={(v) => updateLine(i, { vehicle: v })} test={`line-${i}-vehicle`} readonly={readonly} /></Td>
                  <Td><Inp val={ln.client} on={(v) => updateLine(i, { client: v })} test={`line-${i}-client`} readonly={readonly} /></Td>
                  <Td><Inp val={ln.insurer} on={(v) => updateLine(i, { insurer: v })} test={`line-${i}-insurer`} readonly={readonly} /></Td>
                  <Td className="text-right"><Inp type="number" step="0.01" val={ln.amount_excl} on={(v) => updateLine(i, { amount_excl: Number(v || 0) })} test={`line-${i}-excl`} readonly={readonly} align="right" /></Td>
                  <Td className="text-right text-[#3f4b52]">{money(ln.vat)}</Td>
                  <Td className="text-right"><Inp type="number" step="0.01" val={ln.amount_incl} on={(v) => updateLine(i, { amount_incl: Number(v || 0) })} test={`line-${i}-incl`} readonly={readonly} align="right" /></Td>
                  {!readonly && (
                    <Td>
                      <button onClick={() => rmLine(i)} className="text-[#dc2626] hover:brightness-110"
                        data-testid={`rm-line-${i}`} aria-label="Remove line">
                        <Trash2 size={13} />
                      </button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#f5f6f6]">
              <tr>
                <Td colSpan={5} className="text-right font-display uppercase tracking-widest text-xs text-[#3f4b52]">Subtotal</Td>
                <Td className="text-right font-bold" data-testid="totals-excl">{money(totals.excl)}</Td>
                <Td className="text-right font-bold" data-testid="totals-vat">{money(totals.vat)}</Td>
                <Td className="text-right font-bold text-[#f28a16]" data-testid="totals-incl">{money(totals.incl)}</Td>
                {!readonly && <Td></Td>}
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="p-3 text-[11px] text-[#3f4b52] border-t border-[#cfd7db] bg-[#f5f6f6]">
          Tip: fill in either the <b>Excl.</b> or the <b>Incl.</b> column — the other side computes automatically at {form.vat_pct}% VAT.
        </p>
      </div>

      {/* Notes */}
      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5">
        <Field label="Notes (appears on the PDF footer)">
          <textarea value={form.notes ?? ""} disabled={readonly}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm disabled:bg-[#f5f6f6]"
            data-testid="invoice-notes-input" />
        </Field>
      </div>

      {showPayment && (
        <PaymentRecordingModal
          invoice={{ ...form, id }}
          onClose={() => setShowPayment(false)}
          onUpdated={(fresh) => setForm((f) => ({ ...f, ...fresh }))}
        />
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-2 py-2 font-display uppercase tracking-widest text-xs text-left ${className}`}>{children}</th>
);
const Td = ({ children, className = "", colSpan }) => (
  <td colSpan={colSpan} className={`px-2 py-1.5 ${className}`}>{children}</td>
);

const Inp = ({ val, on, type = "text", step, test, readonly, align }) => (
  <input
    type={type}
    step={step}
    value={val ?? ""}
    disabled={readonly}
    onChange={(e) => on(e.target.value)}
    data-testid={test}
    className={`w-full h-8 px-2 border border-transparent rounded text-xs bg-transparent hover:border-[#cfd7db] focus:border-[#f28a16] focus:bg-white outline-none disabled:bg-[#f5f6f6] ${align === "right" ? "text-right" : ""}`}
  />
);

export default AdminInvoiceForm;
